/**
 * Delivery Management - Admin Dashboard
 * Features:
 * - Auto-create groups by GPS clustering
 * - Manual group creation
 * - Assign/unassign delivery boys
 * - View ungrouped orders
 * - Delete groups
 * - Real-time statistics
 */

import { useState, useEffect } from 'react';
import { 
  MapPin, Package, Users, TrendingUp, Layers, Plus, 
  Trash2, UserCheck, UserX, RefreshCw, Calendar,
  Navigation, Phone, Clock, IndianRupee, Truck,
  ChevronDown, ChevronUp, AlertCircle, CheckCircle
} from 'lucide-react';
import { deliveryManagementAPI } from '../services/deliverySystemAPI';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const DeliveryManagementNew = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('orders_edit');
  const canDelete = hasPermission('orders_delete');

  // State
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [groups, setGroups] = useState([]);
  const [ungroupedOrders, setUngroupedOrders] = useState([]);
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [radiusKm, setRadiusKm] = useState(1);
  const [stats, setStats] = useState({
    totalGroups: 0,
    totalOrders: 0,
    assignedOrders: 0,
    unassignedOrders: 0,
    ungroupedOrders: 0
  });

  // Modals
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [selectedDeliveryBoy, setSelectedDeliveryBoy] = useState('');

  // Load data on mount and date change
  useEffect(() => {
    loadAllData();
  }, [selectedDate]);

  // Helper to get today's date in YYYY-MM-DD format
  function getTodayDate() {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  // Load all data
  const loadAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadGroups(),
        loadUngroupedOrders(),
        loadDeliveryBoys()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Load order groups
  const loadGroups = async () => {
    try {
      const response = await deliveryManagementAPI.getGroups(selectedDate);
      const groupsData = response.data.data || [];
      setGroups(groupsData);
      calculateStats(groupsData, ungroupedOrders);
    } catch (error) {
      console.error('Error loading groups:', error);
      toast.error('Failed to load groups');
    }
  };

  // Load ungrouped orders
  const loadUngroupedOrders = async () => {
    try {
      const response = await deliveryManagementAPI.getUngroupedOrders(selectedDate);
      const ordersData = response.data.data || [];
      setUngroupedOrders(ordersData);
      calculateStats(groups, ordersData);
    } catch (error) {
      console.error('Error loading ungrouped orders:', error);
      toast.error('Failed to load ungrouped orders');
    }
  };

  // Load delivery boys
  const loadDeliveryBoys = async () => {
    try {
      const response = await deliveryManagementAPI.getAvailableDeliveryBoys();
      setDeliveryBoys(response.data.data || []);
    } catch (error) {
      console.error('Error loading delivery boys:', error);
      toast.error('Failed to load delivery boys');
    }
  };

  // Calculate statistics
  const calculateStats = (groupsData, ungroupedData) => {
    const totalOrders = groupsData.reduce((sum, group) => sum + (group.orderCount || group.order_count || 0), 0) + ungroupedData.length;
    const assignedOrders = groupsData.filter(g => g.assigned_delivery_boy_id != null || g.assignedDeliveryBoyId != null).reduce((sum, group) => sum + (group.orderCount || group.order_count || 0), 0);
    const unassignedOrders = groupsData.filter(g => g.assigned_delivery_boy_id == null && g.assignedDeliveryBoyId == null).reduce((sum, group) => sum + (group.orderCount || group.order_count || 0), 0);

    setStats({
      totalGroups: groupsData.length,
      totalOrders,
      assignedOrders,
      unassignedOrders,
      ungroupedOrders: ungroupedData.length
    });
  };

  // Auto-create groups
  const handleAutoCreateGroups = async () => {
    if (!canEdit) {
      toast.error('You do not have permission to create groups');
      return;
    }

    try {
      setLoading(true);
      toast.loading('Creating groups...', { id: 'auto-group' });
      const response = await deliveryManagementAPI.autoCreateGroups(selectedDate, radiusKm);
      
      const { groupsCreated, ordersGrouped } = response.data.data;
      toast.success(`Created ${groupsCreated} groups with ${ordersGrouped} orders!`, { id: 'auto-group' });
      
      await loadAllData();
    } catch (error) {
      console.error('Error auto-creating groups:', error);
      toast.error(error.response?.data?.message || 'Failed to create groups', { id: 'auto-group' });
    } finally {
      setLoading(false);
    }
  };

  // Assign group to delivery boy
  const handleAssignGroup = async () => {
    if (!canEdit) {
      toast.error('You do not have permission to assign groups');
      return;
    }

    if (!selectedDeliveryBoy) {
      toast.error('Please select a delivery boy');
      return;
    }

    try {
      const groupUuid = currentGroup.group_uuid || currentGroup.uuid;
      await deliveryManagementAPI.assignGroup(groupUuid, selectedDeliveryBoy);
      toast.success('Group assigned successfully!');
      setShowAssignModal(false);
      setCurrentGroup(null);
      setSelectedDeliveryBoy('');
      await loadGroups();
    } catch (error) {
      console.error('Error assigning group:', error);
      toast.error(error.response?.data?.message || 'Failed to assign group');
    }
  };

  // Unassign group
  const handleUnassignGroup = async (group) => {
    if (!canEdit) {
      toast.error('You do not have permission to unassign groups');
      return;
    }

    if (!confirm('Are you sure you want to unassign this group?')) return;

    try {
      const groupUuid = group.group_uuid || group.uuid;
      await deliveryManagementAPI.unassignGroup(groupUuid);
      toast.success('Group unassigned successfully!');
      await loadGroups();
    } catch (error) {
      console.error('Error unassigning group:', error);
      toast.error('Failed to unassign group');
    }
  };

  // Delete group
  const handleDeleteGroup = async (group) => {
    if (!canDelete) {
      toast.error('You do not have permission to delete groups');
      return;
    }

    if (!confirm('Are you sure you want to delete this group? Orders will become ungrouped.')) return;

    try {
      const groupUuid = group.group_uuid || group.uuid;
      await deliveryManagementAPI.deleteGroup(groupUuid);
      toast.success('Group deleted successfully!');
      await loadAllData();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group');
    }
  };

  // Open assign modal
  const openAssignModal = (group) => {
    setCurrentGroup(group);
    const assignedTo = group.assignedTo || group.assigned_to;
    setSelectedDeliveryBoy(assignedTo || '');
    setShowAssignModal(true);
  };

  // Extract time slot from group name
  const extractTimeSlot = (groupName) => {
    if (!groupName) return 'Other';
    const parts = groupName.split(' - ');
    if (parts.length > 0) {
      const timeSlot = parts[0].trim().toLowerCase();
      if (timeSlot.includes('morning')) return 'Morning';
      if (timeSlot.includes('evening')) return 'Evening';
      if (timeSlot.includes('afternoon')) return 'Afternoon';
      if (timeSlot.includes('night')) return 'Night';
      return parts[0].trim();
    }
    return 'Other';
  };

  // Get time slot label with formatting
  const getTimeSlotLabel = (timeSlot) => {
    const labelMap = {
      'Morning': '🌅 Morning Orders',
      'Afternoon': '☀️ Afternoon Orders',
      'Evening': '🌆 Evening Orders',
      'Night': '🌙 Night Orders',
      'Other': '📦 Other Orders'
    };
    return labelMap[timeSlot] || `${timeSlot} Orders`;
  };

  // Get time slot icon
  const getTimeSlotIcon = (timeSlot) => {
    const iconMap = {
      'Morning': <span className="text-2xl">🌅</span>,
      'Afternoon': <span className="text-2xl">☀️</span>,
      'Evening': <span className="text-2xl">🌆</span>,
      'Night': <span className="text-2xl">🌙</span>,
      'Other': <span className="text-2xl">📦</span>
    };
    return iconMap[timeSlot] || <Package className="w-6 h-6 text-gray-500" />;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Delivery Management</h1>
          <p className="text-gray-600 mt-1">Auto-group orders and assign to delivery boys</p>
        </div>
        <button
          onClick={loadAllData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Groups</p>
              <p className="text-3xl font-bold text-blue-600">{stats.totalGroups}</p>
            </div>
            <Layers className="w-10 h-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Orders</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalOrders}</p>
            </div>
            <Package className="w-10 h-10 text-gray-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Assigned</p>
              <p className="text-3xl font-bold text-green-600">{stats.assignedOrders}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Unassigned</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.unassignedOrders}</p>
            </div>
            <AlertCircle className="w-10 h-10 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Ungrouped</p>
              <p className="text-3xl font-bold text-red-600">{stats.ungroupedOrders}</p>
            </div>
            <Package className="w-10 h-10 text-red-500" />
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-end gap-4">
          {/* Date Selector */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Delivery Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Radius Selector */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Grouping Radius
            </label>
            <select
              value={radiusKm}
              onChange={(e) => setRadiusKm(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value={1}>1 km</option>
              <option value={2}>2 km</option>
              <option value={3}>3 km</option>
              <option value={5}>5 km</option>
            </select>
          </div>

          {/* Auto-Create Button */}
          <button
            onClick={handleAutoCreateGroups}
            disabled={loading || !canEdit}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
          >
            <Layers className="w-5 h-5" />
            Auto-Create Groups
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-3">
          💡 Auto-grouping uses GPS clustering to group nearby orders within the selected radius
        </p>
      </div>

      {/* Order Groups - Grouped by Time Slot */}
      <div className="space-y-6">
        <h2 className="text-xl font-bold text-gray-900">Order Groups ({groups.length})</h2>
        
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : groups.length === 0 ? (
          <div className="bg-white p-12 rounded-lg shadow-sm border border-gray-200 text-center">
            <Layers className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No order groups found</p>
            <p className="text-gray-500 text-sm mt-2">Click "Auto-Create Groups" to get started</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Group by time slot */}
            {Object.entries(
              groups.reduce((acc, group) => {
                const timeSlot = extractTimeSlot(group.group_name || group.groupName);
                if (!acc[timeSlot]) acc[timeSlot] = [];
                acc[timeSlot].push(group);
                return acc;
              }, {})
            ).map(([timeSlot, timeSlotGroups]) => (
              <div key={timeSlot} className="space-y-3">
                <div className="flex items-center gap-3 mb-4">
                  {getTimeSlotIcon(timeSlot)}
                  <h3 className="text-lg font-semibold text-gray-900">{getTimeSlotLabel(timeSlot)}</h3>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                    {timeSlotGroups.length} groups
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-4 pl-4 border-l-4 border-blue-300">
                  {timeSlotGroups.map((group) => (
                    <GroupCard
                      key={group.id}
                      group={group}
                      deliveryBoys={deliveryBoys}
                      expanded={expandedGroup === group.id}
                      onToggleExpand={() => setExpandedGroup(expandedGroup === group.id ? null : group.id)}
                      onAssign={() => openAssignModal(group)}
                      onUnassign={() => handleUnassignGroup(group)}
                      onDelete={() => handleDeleteGroup(group)}
                      canEdit={canEdit}
                      canDelete={canDelete}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ungrouped Orders */}
      {ungroupedOrders.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-gray-900 text-red-600">
            Ungrouped Orders ({ungroupedOrders.length})
          </h2>
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium text-red-900">These orders need grouping</p>
                <p className="text-sm text-red-700 mt-1">
                  Orders without valid GPS coordinates or outside grouping radius
                </p>
              </div>
            </div>
            <div className="space-y-2">
              {ungroupedOrders.map((order) => (
                <div key={order.uuid} className="bg-white p-4 rounded-lg border border-red-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-900">{order.order_number || 'N/A'}</p>
                      <p className="text-sm text-gray-600 mt-1">{order.customer_name || 'N/A'} - {order.customer_phone || 'N/A'}</p>
                      <p className="text-sm text-gray-500 mt-1">{order.delivery_address_line_1 || 'No address'}</p>
                    </div>
                    <p className="text-lg font-bold text-gray-900">₹{(parseFloat(order.total_amount) || 0).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignModal && (
        <Modal
          title="Assign to Delivery Boy"
          onClose={() => {
            setShowAssignModal(false);
            setCurrentGroup(null);
            setSelectedDeliveryBoy('');
          }}
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">Group: <span className="font-semibold">{currentGroup?.groupName || currentGroup?.group_name}</span></p>
              <p className="text-sm text-gray-600">Orders: <span className="font-semibold">{currentGroup?.orderCount || currentGroup?.order_count || 0}</span></p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Delivery Boy
              </label>
              <select
                value={selectedDeliveryBoy}
                onChange={(e) => setSelectedDeliveryBoy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Select Delivery Boy --</option>
                {deliveryBoys.map((boy) => (
                  <option key={boy.uuid} value={boy.uuid}>
                    {boy.name || boy.full_name || 'N/A'}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setCurrentGroup(null);
                  setSelectedDeliveryBoy('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignGroup}
                disabled={!selectedDeliveryBoy}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Assign Group
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Group Card Component
const GroupCard = ({ group, deliveryBoys, expanded, onToggleExpand, onAssign, onUnassign, onDelete, canEdit, canDelete }) => {
  // Handle both camelCase and snake_case from backend
  const assignedDeliveryBoyId = group.assigned_delivery_boy_id || group.assignedDeliveryBoyId;
  const deliveryBoyName = group.delivery_boy_name || group.deliveryBoyName;
  const groupName = group.groupName || group.group_name;
  const orderCount = group.orderCount || group.order_count || 0;
  const deliveryDate = group.deliveryDate || group.delivery_date;
  const groupUuid = group.group_uuid || group.groupUuid;
  
  // Extract time slot for display
  const extractTimeSlotForCard = (name) => {
    if (!name) return null;
    const parts = name.split(' - ');
    return parts[0].trim();
  };
  
  const timeSlot = extractTimeSlotForCard(groupName);
  
  // Check if assigned - if assignedDeliveryBoyId exists, it's assigned
  const isAssigned = assignedDeliveryBoyId != null;

  return (
    <div className={`bg-white rounded-lg shadow-sm border-2 transition-all ${
      isAssigned ? 'border-green-200' : 'border-yellow-200'
    }`}>
      <div className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3 className="text-lg font-semibold text-gray-900">{groupName}</h3>
              {timeSlot && (
                <span className="px-2.5 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-semibold">
                  {timeSlot}
                </span>
              )}
              {isAssigned ? (
                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Assigned
                </span>
              ) : (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full font-medium flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Unassigned
                </span>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Orders</p>
                <p className="font-semibold text-gray-900">{orderCount}</p>
              </div>
              <div>
                <p className="text-gray-600">Delivery Date</p>
                <p className="font-semibold text-gray-900">
                  {deliveryDate ? new Date(deliveryDate).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              {isAssigned && deliveryBoyName && (
                <div>
                  <p className="text-gray-600">Assigned To</p>
                  <p className="font-semibold text-gray-900">{deliveryBoyName}</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 ml-4">
            {isAssigned ? (
              canEdit && (
                <button
                  onClick={onUnassign}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Unassign"
                >
                  <UserX className="w-5 h-5" />
                </button>
              )
            ) : (
              canEdit && (
                <button
                  onClick={onAssign}
                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                  title="Assign"
                >
                  <UserCheck className="w-5 h-5" />
                </button>
              )
            )}
            {canDelete && (
              <button
                onClick={onDelete}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete Group"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onToggleExpand}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Expanded Content - Show Group Members */}
        {expanded && group.orders && group.orders.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-3">Orders in this group:</h4>
            <div className="space-y-2">
              {group.orders.map((order) => (
                <div key={order.uuid} className="bg-gray-50 p-3 rounded-lg text-sm">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{order.order_number || 'N/A'}</p>
                      <p className="text-gray-600 mt-1">{order.customer_name || 'N/A'} - {order.customer_phone || 'N/A'}</p>
                      <p className="text-gray-500 text-xs mt-1">{order.delivery_address_line_1 || 'No address'}</p>
                    </div>
                    <p className="font-bold text-gray-900">₹{(parseFloat(order.total_amount) || 0).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Modal Component
const Modal = ({ title, children, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ×
          </button>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default DeliveryManagementNew;